import { useEffect, useState } from "react";
import { supabase } from "@/shared/integrations/supabase/client";

/**
 * Resolves Learning evidence photo paths to displayable URLs.
 *
 * The queue returns `asset_documents.file_path` verbatim, which is EITHER a full
 * https URL (legacy field-app uploads — usable as-is) OR a Supabase storage key
 * (needs a signed URL). The Learning card previously rendered the raw value, so
 * storage-key photos showed as a broken <img>. This mirrors the resolution the
 * rest of the app already does (see assets' usePhotoSignedUrls): pass through
 * http(s), sign the rest against the `asset-documents` bucket under the caller's
 * own RLS (so a client only signs photos they can access).
 *
 * Returns one entry per input path, index-aligned, with three distinguishable
 * states so the caller can render the right thing: `undefined` = still resolving
 * (show a spinner), `null` = resolution failed (RLS denial / missing object —
 * show an "unavailable" fallback, not a perpetual spinner), `string` = ready.
 */
export function useEvidencePhotoUrls(paths: string[]): (string | null | undefined)[] {
  const [urls, setUrls] = useState<(string | null | undefined)[]>(() => paths.map(() => undefined));
  // Join is a stable dependency key for the path list (avoids re-running on each
  // render from a fresh array identity).
  const key = paths.join("|");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const resolved = await Promise.all(
        paths.map(async (p) => {
          if (!p) return null;
          if (p.startsWith("http://") || p.startsWith("https://")) return p;
          const { data } = await supabase.storage
            .from("asset-documents")
            .createSignedUrl(p, 3600); // 1 hour, matches usePhotoSignedUrls
          return data?.signedUrl ?? null;
        }),
      );
      if (!cancelled) setUrls(resolved);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return urls;
}
